// AuthProvider — single React-Query-backed source of truth for current user.
// Per W2 §7.1: copy v2 useMeQuery verbatim + normalize 401 → null.

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMe, type MeResponse, type User } from "@/lib/api/auth";
import { queryKeys } from "@/lib/queries/keys";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  pairId: string | null;
  refresh: () => Promise<unknown>;
  /**
   * True when this device *had* a session and no longer does, without anyone
   * asking it to end.
   *
   * w2.md §4.7 mandates "rotate-on-action: login / change-password delete other
   * sessions", so signing in on a second device silently revokes the first. The
   * query above is re-checked to notice that (see the trigger comment there).
   * What was missing is the last step: `RequireAuth` redirects to
   * `/login?next=…` with no reason at all, so the person is dropped onto a bare
   * form and re-types a password that worked a minute ago — and that re-login
   * revokes the device they were *trying* to keep. The copy for it already
   * existed in `AUTH_I18N.session_expired` and was never rendered anywhere.
   */
  revoked: boolean;
  /** Called by the sign-out flow so an intentional logout is not reported as a revocation. */
  markSignedOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  // `isLoading` only — NOT `isFetching`. See the `settled` note below.
  const { data, isLoading } = useQuery<MeResponse | null>({
    queryKey: queryKeys.auth.session(),
    queryFn: fetchMe,
    // Re-check on window focus, and not on a timer (W40.1 F1, supersedes the
    // `refetchInterval: 30_000` this used to carry).
    //
    // The poll's only job is to notice "this session was deleted by signing in
    // on another device" (w2.md §4.7 rotate-on-action). The moment a human is
    // willing to act on a dead page — by typing into it, tapping it — is the
    // moment they have just come back to the tab, which is precisely what
    // `refetchOnWindowFocus` covers. A timer fires while they are *in* the page,
    // which is the one time a re-auth check is pure cost.
    //
    // And the timer was not merely wasteful. `refetchInterval` puts
    // `isFetching` true every 30 s, the old `settled` read
    // `!isLoading && !isFetching`, so `status` flipped back to `"loading"` on
    // every tick and `RequireAuth` replaced the whole `<AppShell/>` subtree
    // with a full-screen spinner until it settled. Every 30 seconds the app
    // unmounted and remounted itself, and every unsaved draft, open sheet and
    // half-typed field in local state went with it. Measured on the deployed
    // app: a form being filled was wiped every 30 s.
    //
    // `refetchOnWindowFocus: false` is deleted with the interval: v5 already
    // defaults it to `true`, and the override is what suppressed the very
    // trigger that now covers the case.
    staleTime: 30_000,
    retry: false,
  });

  // Stable `refresh` reference — using useCallback with only queryClient in
  // deps means consumers can safely put `refresh` in their own useEffect
  // dependency array without triggering infinite re-runs.
  //
  // Critical: previously this was a fresh closure inside useMemo, which
  // changed reference every time the auth query state mutated. DevLoginRoute
  // had `refresh` in its useEffect deps, so every state change re-ran the
  // effect, which called `refresh()` again, which mutated state again, … →
  // infinite loop. Login/Register avoided this because they call refresh()
  // inside onSubmit (not useEffect). The fix: stabilize refresh here so all
  // consumers are safe.
  //
  // Note: we deliberately do NOT call `refetch()` after invalidateQueries when
  // the query is active — `invalidateQueries({ refetchType: 'active' })`
  // already triggers an immediate refetch on every active observer, and the
  // returned promise resolves when that refetch settles. Calling refetch()
  // again would issue a second redundant request.
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.auth.session(),
      refetchType: "active",
    });
  }, [queryClient]);

  // "Did this device ever hold a session?" — a ref rather than state because it
  // only ever moves false -> true, and writing it during render (idempotently)
  // is what lets `revoked` be correct on the very render that first shows the
  // 401, without an extra render pass.
  const hadSession = useRef(false);
  if (data) hadSession.current = true;

  // Set by the logout button. Without it, signing out and being signed out
  // would look identical from here, and the user would be told their session
  // expired when they pressed the button themselves.
  const [signedOut, setSignedOut] = useState(false);
  const markSignedOut = useCallback(() => setSignedOut(true), []);

  const value = useMemo<AuthContextValue>(() => {
    let status: AuthStatus = "loading";
    // `isLoading` alone, and that is the whole point (W40.1 F1).
    //
    // React Query v5 defines `isLoading` as `isPending && isFetching` — "this
    // query key has never produced data yet". A *background* refetch leaves
    // `isLoading` false and only raises `isFetching`, so folding `isFetching`
    // into this term made every re-check a cold start: `status` went back to
    // `"loading"` and `RequireAuth` swapped the entire app tree for a spinner.
    //
    // `revoked` is gated by the same term on purpose. While the first load is
    // in flight there is nothing to have been revoked, and a `false` there would
    // let "read the reason but still passed" cases through — which is exactly
    // how the "a visitor who never had a session" case used to pass for the
    // wrong reason.
    const settled = !isLoading;
    if (settled) {
      status = data ? "authenticated" : "anonymous";
    }
    // A fresh successful login clears the marker, so signing back in does not
    // leave the next 401 looking like the first one.
    const revoked = settled && hadSession.current && !data && !signedOut;
    return {
      status,
      user: data?.user ?? null,
      pairId: data?.pair_id ?? null,
      refresh,
      revoked,
      markSignedOut,
    };
  }, [data, isLoading, refresh, signedOut, markSignedOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Allow non-component exports alongside <AuthProvider/> — `useAuth` hook is
// intentionally co-located for the consumer DX (per v2 §3.3.2 #4).
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be called inside <AuthProvider>");
  }
  return ctx;
}
